/**
 * Track change parser — maps OOXML <w:ins> and <w:del> directly to ProseMirror marks.
 *
 * In OOXML, track changes wrap content:
 *   <w:ins w:id="N" w:author="Name" w:date="ISO-8601">
 *     <w:r><w:t>Inserted text</w:t></w:r>
 *   </w:ins>
 *
 *   <w:del w:id="N" w:author="Name" w:date="ISO-8601">
 *     <w:r><w:delText>Deleted text</w:delText></w:r>
 *   </w:del>
 *
 * These are siblings of <w:r> within <w:p> (or can contain <w:r> elements).
 *
 * Our approach:
 *   1. When we encounter <w:ins>, parse its child runs normally and apply insertion marks
 *   2. When we encounter <w:del>, parse <w:delText> as visible text and apply deletion marks
 *   3. Record metadata (author, date, text) for each track change
 *
 * TipTap mark attrs:
 *   insertion: { type: "insertion", attrs: { changeId, author, date } }
 *   deletion:  { type: "deletion", attrs: { changeId, author, date } }
 */

import { PMNode, PMMark, ParseContext, ParsedTrackChange } from './types';
import { getChildNS, parseRunPropertiesFromElement, W_NS } from './zipReader';
import { runPropertiesToMarks } from './runParser';
import { CommentTracker } from './commentParser';

/**
 * Parse a <w:ins> element — returns text nodes with insertion marks.
 */
export function parseInsertion(
  insEl: Element,
  ctx: ParseContext,
  tracker: TrackChangeTracker
): PMNode[] {
  const author =
    insEl.getAttributeNS(W_NS, 'author') ||
    insEl.getAttribute('w:author') ||
    '';
  const date =
    insEl.getAttributeNS(W_NS, 'date') || insEl.getAttribute('w:date') || '';

  const changeId = tracker.registerChange('insertion', author, date);

  const insertionMark: PMMark = {
    type: 'insertion',
    attrs: { changeId, author, date },
  };

  // Parse child runs normally, then add insertion mark to each text node
  const nodes: PMNode[] = [];
  const children = insEl.childNodes;
  const commentTracker = ctx.commentTracker as CommentTracker | undefined;

  for (let i = 0; i < children.length; i++) {
    const child = children[i];
    if (child.nodeType !== 1) continue;
    const el = child as Element;
    const localName = el.localName || el.nodeName.replace(/^w:/, '');

    if (localName === 'r') {
      const runNodes = parseRunWithExtraMarks(el, [insertionMark], ctx);
      // Record text for metadata
      for (const node of runNodes) {
        if (node.text) tracker.recordText(changeId, node.text);
      }
      nodes.push(...runNodes);
    } else if (localName === 'commentRangeStart') {
      const id = el.getAttributeNS(W_NS, 'id') || el.getAttribute('w:id') || '';
      if (id && commentTracker) commentTracker.handleCommentRangeStart(id);
    } else if (localName === 'commentRangeEnd') {
      const id = el.getAttributeNS(W_NS, 'id') || el.getAttribute('w:id') || '';
      if (id && commentTracker) commentTracker.handleCommentRangeEnd(id);
    }
  }

  return nodes;
}

/**
 * Parse a <w:del> element — returns text nodes with deletion marks.
 *
 * Key difference from insertions: deleted text uses <w:delText> instead of <w:t>.
 * Mammoth's adapter had to convert delText→t and move content out of <w:del>.
 * We handle it natively — much simpler.
 */
export function parseDeletion(
  delEl: Element,
  ctx: ParseContext,
  tracker: TrackChangeTracker
): PMNode[] {
  const author =
    delEl.getAttributeNS(W_NS, 'author') ||
    delEl.getAttribute('w:author') ||
    '';
  const date =
    delEl.getAttributeNS(W_NS, 'date') || delEl.getAttribute('w:date') || '';

  const changeId = tracker.registerChange('deletion', author, date);

  const deletionMark: PMMark = {
    type: 'deletion',
    attrs: { changeId, author, date },
  };

  const nodes: PMNode[] = [];
  const children = delEl.childNodes;
  const commentTracker = ctx.commentTracker as CommentTracker | undefined;

  for (let i = 0; i < children.length; i++) {
    const child = children[i];
    if (child.nodeType !== 1) continue;
    const el = child as Element;
    const localName = el.localName || el.nodeName.replace(/^w:/, '');

    if (localName === 'r') {
      // Parse run but use <w:delText> instead of <w:t>
      const runNodes = parseDeletedRun(el, [deletionMark], ctx);
      for (const node of runNodes) {
        if (node.text) tracker.recordText(changeId, node.text);
      }
      nodes.push(...runNodes);
    } else if (localName === 'commentRangeStart') {
      const id = el.getAttributeNS(W_NS, 'id') || el.getAttribute('w:id') || '';
      if (id && commentTracker) commentTracker.handleCommentRangeStart(id);
    } else if (localName === 'commentRangeEnd') {
      const id = el.getAttributeNS(W_NS, 'id') || el.getAttribute('w:id') || '';
      if (id && commentTracker) commentTracker.handleCommentRangeEnd(id);
    }
  }

  return nodes;
}

/**
 * Parse a run inside <w:ins> — same as normal run but with extra marks added.
 */
function parseRunWithExtraMarks(
  runEl: Element,
  extraMarks: PMMark[],
  ctx: ParseContext
): PMNode[] {
  const nodes: PMNode[] = [];

  const rPrEl = getChildNS(runEl, W_NS, 'rPr');
  const runProps = rPrEl ? parseRunPropertiesFromElement(rPrEl) : {};
  const baseMarks = runPropertiesToMarks(runProps);
  const allMarks = [...baseMarks, ...extraMarks];

  const children = runEl.childNodes;
  for (let i = 0; i < children.length; i++) {
    const child = children[i];
    if (child.nodeType !== 1) continue;
    const el = child as Element;
    const localName = el.localName || el.nodeName.replace(/^w:/, '');

    if (localName === 't') {
      const text = el.textContent || '';
      if (text.length > 0) {
        const textNode: PMNode = { type: 'text', text };
        if (allMarks.length > 0) textNode.marks = [...allMarks];
        nodes.push(textNode);
      }
    } else if (localName === 'br') {
      nodes.push({ type: 'hardBreak' });
    } else if (localName === 'tab') {
      const tabNode: PMNode = { type: 'text', text: '\t' };
      if (allMarks.length > 0) tabNode.marks = [...allMarks];
      nodes.push(tabNode);
    }
  }

  return nodes;
}

/**
 * Parse a run inside <w:del> — reads <w:delText> instead of <w:t>.
 * This is the key difference: mammoth ignores <w:delText>, we read it directly.
 */
function parseDeletedRun(
  runEl: Element,
  extraMarks: PMMark[],
  ctx: ParseContext
): PMNode[] {
  const nodes: PMNode[] = [];

  const rPrEl = getChildNS(runEl, W_NS, 'rPr');
  const runProps = rPrEl ? parseRunPropertiesFromElement(rPrEl) : {};
  const baseMarks = runPropertiesToMarks(runProps);
  const allMarks = [...baseMarks, ...extraMarks];

  const children = runEl.childNodes;
  for (let i = 0; i < children.length; i++) {
    const child = children[i];
    if (child.nodeType !== 1) continue;
    const el = child as Element;
    const localName = el.localName || el.nodeName.replace(/^w:/, '');

    // <w:delText> is the deleted text content (equivalent of <w:t> for deleted runs)
    if (localName === 'delText' || localName === 't') {
      const text = el.textContent || '';
      if (text.length > 0) {
        const textNode: PMNode = { type: 'text', text };
        if (allMarks.length > 0) textNode.marks = [...allMarks];
        nodes.push(textNode);
      }
    } else if (localName === 'br') {
      nodes.push({ type: 'hardBreak' });
    } else if (localName === 'tab') {
      const tabNode: PMNode = { type: 'text', text: '\t' };
      if (allMarks.length > 0) tabNode.marks = [...allMarks];
      nodes.push(tabNode);
    }
  }

  return nodes;
}

/**
 * Tracks all track changes during document parsing.
 */
export class TrackChangeTracker {
  private changes: Map<string, ParsedTrackChange> = new Map();
  private hasChanges = false;

  /** Register a new track change and return its ID. */
  registerChange(
    type: 'insertion' | 'deletion',
    author: string,
    date: string
  ): string {
    this.hasChanges = true;
    const changeId = generateChangeId();
    this.changes.set(changeId, {
      id: changeId,
      type,
      author,
      date,
      text: '',
    });
    return changeId;
  }

  /** Record text content for a track change. */
  recordText(changeId: string, text: string): void {
    const change = this.changes.get(changeId);
    if (change) {
      change.text += text;
    }
  }

  /** Whether any track changes were found. */
  get foundTrackChanges(): boolean {
    return this.hasChanges;
  }

  /** Get all parsed track changes. */
  getParsedTrackChanges(): ParsedTrackChange[] {
    return Array.from(this.changes.values());
  }
}

/** Generate a unique TipTap change ID. */
let changeCounter = 0;
export function generateChangeId(): string {
  changeCounter++;
  const timestamp = Date.now();
  const random = Math.floor(Math.random() * 100000);
  return `change-${timestamp}-${random}-${changeCounter}`;
}

/** Reset counter (for testing). */
export function resetChangeCounter(): void {
  changeCounter = 0;
}
